import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ArrowUpRight, ArrowDownRight, Globe, ChevronDown, Copy, Check, Search } from 'lucide-react';
import { toast } from 'sonner';
import TrafficInsights from '@/components/TrafficInsights';
import AreaChart from '@/components/AreaChart';
import PagesInsights from '@/components/PagesInsights';
import LocationsInsights from '@/components/LocationsInsights';
import PeriodSelector from '@/components/PeriodSelector';
import ComparisonPicker from '@/components/ComparisonPicker';
import GoalConversions from '@/components/GoalConversions';
import DevicesInsights from '@/components/DevicesInsights';
import FilterBanners from '@/components/FilterBanners';
import HeaderBar from '@/components/HeaderBar';
import SearchQueriesInsights from '@/components/SearchQueriesInsights';
import PageFilterModal from '@/components/PageFilterModal';
import LocationFilterModal from '@/components/LocationFilterModal';
import DeviceFilterModal from '@/components/DeviceFilterModal';
import BrowserFilterModal from '@/components/BrowserFilterModal';
import OSFilterModal from '@/components/OSFilterModal';
import GoalFilterModal from '@/components/GoalFilterModal';
import SourceFilterModal from '@/components/SourceFilterModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Pure helpers (no React state) — exported nowhere, just kept module-local.
// ---------------------------------------------------------------------------

/**
 * Append the standard analytics query params to a base URL.
 * Pass only the filters that apply; `null`/`undefined` are skipped.
 * `pageFilters` is the parsed array of `{condition, type, value}` objects.
 */
const buildAnalyticsQueryParams = (baseUrl, {
        period,
        startDate, endDate,
        channel, location, device, os, browser, goal,
        pageFilters = [],
        // Period-over-period comparison — propagate to every analytics fetch so
        // the backend computes prev_* against the right window (or skips it when
        // `comparison=none`). When the params are unset the backend falls back
        // to its legacy "previous period" behavior for backward compat.
        comparison, comparisonMatch, comparisonStart, comparisonEnd,
}) => {
        let url = `${baseUrl}?period=${period}`;
        if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
        if (channel) url += `&channel=${encodeURIComponent(channel)}`;
        if (location) url += `&location=${encodeURIComponent(location)}`;
        if (device) url += `&device=${encodeURIComponent(device)}`;
        if (os) url += `&os=${encodeURIComponent(os)}`;
        if (browser) url += `&browser=${encodeURIComponent(browser)}`;
        if (goal) url += `&goal=${encodeURIComponent(goal)}`;
        if (comparison) url += `&comparison=${encodeURIComponent(comparison)}`;
        if (comparisonMatch) url += `&comparison_match=${encodeURIComponent(comparisonMatch)}`;
        if (comparisonStart) url += `&comparison_start=${encodeURIComponent(comparisonStart)}`;
        if (comparisonEnd) url += `&comparison_end=${encodeURIComponent(comparisonEnd)}`;
        pageFilters.forEach(filter => {
                url += `&f=${filter.condition},${filter.type},${encodeURIComponent(filter.value)}`;
        });
        return url;
};

/**
 * Replace every URL `f=condition,type,value` entry whose `type` is in
 * `typesToReplace` with the provided `newFilterStrings`, preserving any
 * other `f` entries untouched. Returns a fresh URLSearchParams.
 */
const replaceFiltersOfTypes = (searchParams, typesToReplace, newFilterStrings) => {
        const params = new URLSearchParams(searchParams);
        const existing = params.getAll('f');
        params.delete('f');
        existing.forEach(f => {
                const parts = f.split(',');
                if (parts.length >= 2 && !typesToReplace.includes(parts[1])) {
                        params.append('f', f);
                }
        });
        newFilterStrings.forEach(f => params.append('f', f));
        return params;
};

const AnalyticsPage = ({ user, token, logout, publicMode = false, websiteIdOverride = null, domainOverride = null }) => {
        // In publicMode the page is rendered by PublicAnalyticsPage with a website_id
        // resolved from /api/public/{domain}. Otherwise we read the id from the route.
        const params = useParams();
        const websiteId = websiteIdOverride || params.websiteId;
        // Helper: authorized requests get a Bearer token; public requests omit it
        // (the backend analytics endpoints accept either via get_current_user_optional).
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        // Frontend route base — filter clicks and chart navigations push to the
        // current page so a public viewer stays on /site/{domain} instead of bouncing
        // to the gated /analytics/{id} route.
        // Defensively strip scheme / www / trailing slash from the domain before
        // splicing it into the path: the backend stores domains with the scheme
        // (e.g. "https://www.yogabox.com"), and embedding that verbatim into a
        // route segment breaks React Router (slashes become path separators).
        const routeDomain = (domainOverride || '')
                .replace(/^https?:\/\//, '')
                .replace(/^www\./, '')
                .replace(/\/$/, '');
        const routeBase = publicMode && routeDomain
                ? `/site/${routeDomain}`
                : `/analytics/${websiteId}`;
        const [searchParams] = useSearchParams();
        const navigate = useNavigate();
        const [website, setWebsite] = useState(null);
        const [analytics, setAnalytics] = useState(null);
        const [realtime, setRealtime] = useState(null);
        const [loading, setLoading] = useState(true);
        const [copied, setCopied] = useState(false);
        const [chartMode, setChartMode] = useState('visitors');
        const [chartLoading, setChartLoading] = useState(false);
        const [showFilterDropdown, setShowFilterDropdown] = useState(false);
        const [showPageFilterModal, setShowPageFilterModal] = useState(false);
        const [showLocationFilterModal, setShowLocationFilterModal] = useState(false);
        const [showDeviceFilterModal, setShowDeviceFilterModal] = useState(false);
        const [showBrowserFilterModal, setShowBrowserFilterModal] = useState(false);
        const [showOsFilterModal, setShowOsFilterModal] = useState(false);
        const [showGoalFilterModal, setShowGoalFilterModal] = useState(false);
        const [showSourceFilterModal, setShowSourceFilterModal] = useState(false);



        const filterRef = useRef(null);
        const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, data: null });
        const [accessRole, setAccessRole] = useState(null); // User's role for this website
        const fetchingRef = useRef(false); // Prevent duplicate API calls
        const [goalConversionData, setGoalConversionData] = useState(null); // Store goal conversion data when filter is active

        // Get all filter parameters from URL
        const period = searchParams.get('period') || 'today';
        const startDate = searchParams.get('start_date');
        const endDate = searchParams.get('end_date');
        const channel = searchParams.get('channel');
        const location = searchParams.get('location');
        const device = searchParams.get('device');
        const os = searchParams.get('os');
        const browser = searchParams.get('browser');
        const goal = searchParams.get('goal');
        // Period-over-period comparison params. Absent = comparison off
        // (the backend treats undefined comparison as legacy "always-on previous_period"
        // for back-compat; the picker writes an explicit value once toggled).
        const comparison = searchParams.get('comparison');
        const comparisonMatch = searchParams.get('comparison_match');
        const comparisonStartParam = searchParams.get('comparison_start');
        const comparisonEndParam = searchParams.get('comparison_end');
        const comparisonActive = !!comparison && comparison !== 'none';
        
        // Parse page filters from URL - use useMemo for stable reference
        const pageFilters = useMemo(() => {
                return searchParams.getAll('f').map(f => {
                        const parts = f.split(',');
                        if (parts.length >= 3) {
                                return {
                                        condition: parts[0],
                                        type: parts[1],
                                        value: parts.slice(2).join(',')
                                };
                        }
                        return null;
                }).filter(Boolean);
        }, [searchParams]);
        
        // Create stable string key for pageFilters to prevent unnecessary re-renders
        const pageFiltersKey = useMemo(() => searchParams.getAll('f').sort().join('|'), [searchParams]);
        
        // Check if there's an active goal filter (from pageFilters or legacy goal param)
        const activeGoalFilter = useMemo(() => {
                // Check pageFilters for goal type
                const goalFilter = pageFilters.find(f => f.type === 'goal');
                if (goalFilter) {
                        // Extract goal info from value (format: "id:name" or "id1:name1|id2:name2")
                        const firstGoal = goalFilter.value.split('|')[0];
                        const [goalId, goalName] = firstGoal.includes(':') 
                                ? [firstGoal.split(':')[0], firstGoal.split(':').slice(1).join(':')]
                                : [firstGoal, null];
                        return { id: goalId, name: goalName, condition: goalFilter.condition };
                }
                // Fallback to legacy goal param
                if (goal) {
                        const [goalId, goalName] = goal.includes(':') 
                                ? [goal.split(':')[0], goal.split(':').slice(1).join(':')]
                                : [goal, null];
                        return { id: goalId, name: goalName, condition: 'is' };
                }
                return null;
        }, [pageFilters, goal]);
        
        // Fetch goal conversion data when goal filter is active
        const fetchGoalConversionData = useCallback(async () => {
                // In publicMode there is no token; the goal-conversions endpoint accepts
                // unauthenticated requests for public sites via get_current_user_optional.
                if (!activeGoalFilter || !websiteId || (!token && !publicMode)) {
                        setGoalConversionData(null);
                        return;
                }
                
                try {
                        let url = `${API}/analytics/${websiteId}/goal-conversions?period=${period}`;
                        if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
                        if (channel) url += `&channel=${encodeURIComponent(channel)}`;
                        if (location) url += `&location=${encodeURIComponent(location)}`;
                        if (device) url += `&device=${encodeURIComponent(device)}`;
                        if (os) url += `&os=${encodeURIComponent(os)}`;
                        if (browser) url += `&browser=${encodeURIComponent(browser)}`;
                        
                        const res = await axios.get(url, {
                                headers: authHeaders
                        });
                        
                        const items = res.data.items || [];
                        const selectedGoal = items.find(g => g.id === activeGoalFilter.id);
                        setGoalConversionData(selectedGoal || null);
                } catch (error) {
                        console.error('Failed to fetch goal conversion data:', error);
                        setGoalConversionData(null);
                }
        }, [activeGoalFilter, websiteId, token, period, startDate, endDate, channel, location, device, os, browser]);
        
        // Fetch goal data when filter changes
        useEffect(() => {
                fetchGoalConversionData();
        }, [fetchGoalConversionData]);
        
        // Permission helpers
        const canEdit = accessRole && ['owner', 'admin', 'editor'].includes(accessRole.role);
        const canViewSettings = accessRole && ['owner', 'admin', 'editor'].includes(accessRole.role);
        const canManageBilling = accessRole && ['owner', 'billing'].includes(accessRole.role);

        const fetchAccessRole = useCallback(async () => {
                // In publicMode there is no signed-in user, so the access endpoint would
                // 401. Skip — canEdit / canViewSettings stay false and owner-only UI hides.
                if (publicMode) {
                        setAccessRole(null);
                        return;
                }
                try {
                        const response = await axios.get(`${API}/websites/${websiteId}/access`, {
                                headers: authHeaders
                        });
                        setAccessRole(response.data);
                } catch (error) {
                        console.error('Failed to fetch access role:', error);
                }
        }, [websiteId, token, publicMode]);

        const fetchData = useCallback(async () => {
                // Prevent duplicate calls
                if (fetchingRef.current) {
                        return;
                }
                fetchingRef.current = true;
                setChartLoading(true);

                try {
                        const filterArgs = {
                                period, startDate, endDate,
                                channel, location, device, os, browser, goal, pageFilters,
                                // Comparison params — same plumbing for every analytics URL
                                comparison, comparisonMatch,
                                comparisonStart: comparisonStartParam, comparisonEnd: comparisonEndParam,
                        };

                        // Build URLs for parallel requests - always use 'visitors' mode for initial load
                        const metricsUrl = buildAnalyticsQueryParams(`${API}/analytics/${websiteId}/metrics`, filterArgs);
                        const chartUrl = buildAnalyticsQueryParams(`${API}/analytics/${websiteId}/chart-data`, filterArgs) + `&mode=visitors`;
                        const geoUrl = buildAnalyticsQueryParams(`${API}/analytics/${websiteId}/geo-stats`, filterArgs);
                        const techUrl = buildAnalyticsQueryParams(`${API}/analytics/${websiteId}/tech-stats`, filterArgs);
                        const pagesUrl = buildAnalyticsQueryParams(`${API}/analytics/${websiteId}/pages-stats`, filterArgs);

                        // Fetch all data in parallel.
                        // In publicMode we skip /websites (requires auth) and seed the website
                        // object from the domainOverride prop instead — the public viewer doesn't
                        // need the full website list, just the current site's display name.
                        const websitesPromise = publicMode
                                ? Promise.resolve({ data: [{ id: websiteId, domain: domainOverride || '' }] })
                                : axios.get(`${API}/websites`, { headers: authHeaders });
                        const [websitesRes, metricsRes, chartRes, geoRes, techRes, pagesRes, realtimeRes] = await Promise.all([
                                websitesPromise,
                                axios.get(metricsUrl, { headers: authHeaders }),
                                axios.get(chartUrl, { headers: authHeaders }),
                                axios.get(geoUrl, { headers: authHeaders }),
                                axios.get(techUrl, { headers: authHeaders }),
                                axios.get(pagesUrl, { headers: authHeaders }),
                                axios.get(`${API}/analytics/${websiteId}/realtime`, { headers: authHeaders })
                        ]);

                        // Find website
                        const site = websitesRes.data.find(w => w.id === websiteId);
                        setWebsite(site);

                        // Combine all analytics data
                        const combinedAnalytics = {
                                ...metricsRes.data,
                                ...chartRes.data,
                                ...geoRes.data,
                                ...techRes.data,
                                ...pagesRes.data
                        };

                        setAnalytics(combinedAnalytics);
                        setRealtime(realtimeRes.data);
                        setChartMode('visitors'); // Reset to visitors on data reload
                        setLoading(false);
                        setChartLoading(false);
                } catch (err) {
                        console.error('Analytics fetch error:', err);
                        toast.error('Failed to load analytics');
                        setLoading(false);
                        setChartLoading(false);
                } finally {
                        fetchingRef.current = false;
                }
        }, [
                websiteId, token,
                period, startDate, endDate,
                channel, location, device, os, browser, goal, pageFiltersKey,
                // Refetch on any comparison change so prev_* stays in sync with the picker
                comparison, comparisonMatch, comparisonStartParam, comparisonEndParam,
        ]);

        const fetchRealtime = useCallback(async () => {
                try {
                        const res = await axios.get(`${API}/analytics/${websiteId}/realtime`, {
                                headers: authHeaders
                        });
                        setRealtime(res.data);
                } catch (err) {
                        // Silent fail for realtime updates
                }
        }, [websiteId, token]);

        // Separate function to fetch only chart data when clicking metric boxes
        const fetchChartDataOnly = async (mode) => {
                setChartLoading(true);
                try {
                        const chartUrl = buildAnalyticsQueryParams(
                                `${API}/analytics/${websiteId}/chart-data`,
                                {
                                        period, startDate, endDate,
                                        channel, location, device, os, browser, goal, pageFilters,
                                        comparison, comparisonMatch,
                                        comparisonStart: comparisonStartParam, comparisonEnd: comparisonEndParam,
                                },
                        ) + `&mode=${mode}`;

                        const chartRes = await axios.get(chartUrl, { headers: authHeaders });

                        // Update only chart-related data in analytics state
                        setAnalytics(prev => ({
                                ...prev,
                                ...chartRes.data,
                        }));
                } catch (err) {
                        console.error('Chart data fetch error:', err);
                        toast.error('Failed to load chart data');
                } finally {
                        setChartLoading(false);
                }
        };

        // Handler for metric card clicks
        const handleMetricClick = (mode) => {
                setChartMode(mode);
                fetchChartDataOnly(mode);
        };

        useEffect(() => {
                fetchAccessRole();
        }, [websiteId, token]);
        
        useEffect(() => {
                fetchData();
        }, [fetchData]);
        
        useEffect(() => {
                const interval = setInterval(fetchRealtime, 10000);
                return () => clearInterval(interval);
        }, [fetchRealtime]);

        // Close filter dropdown when clicking outside
        useEffect(() => {
                const handleClickOutside = (event) => {
                        if (filterRef.current && !filterRef.current.contains(event.target)) {
                                setShowFilterDropdown(false);
                        }
                };
                document.addEventListener('mousedown', handleClickOutside);
                return () => document.removeEventListener('mousedown', handleClickOutside);
        }, []);

        // Clear a specific page filter
        const clearPageFilter = (filterIndex) => {
                const params = new URLSearchParams(searchParams);
                const currentFilters = params.getAll('f');
                params.delete('f');
                currentFilters.forEach((f, idx) => {
                        if (idx !== filterIndex) {
                                params.append('f', f);
                        }
                });
                navigate(`${routeBase}?${params.toString()}`);
        };

        // -------------------------------------------------------------------
        // Filter mutators — all share `replaceFiltersOfTypes` so behavior stays
        // identical to before but the code lives in one place.
        // Each `filters` argument is an array of "condition,type,value" strings.
        // -------------------------------------------------------------------
        const applyFiltersAndNavigate = (typesToReplace, filters) => {
                const params = replaceFiltersOfTypes(searchParams, typesToReplace, filters);
                navigate(`${routeBase}?${params.toString()}`);
        };

        const applyPageFilter = (filters) =>
                applyFiltersAndNavigate(['page', 'entry_page', 'exit_page'], filters);

        const applyLocationFilters = (filters) =>
                applyFiltersAndNavigate(['country', 'region', 'city'], filters);

        const applyDeviceFilters = (filters) =>
                applyFiltersAndNavigate(['device'], filters);

        const applyBrowserFilters = (filters) =>
                applyFiltersAndNavigate(['browser'], filters);

        const applyOsFilters = (filters) =>
                applyFiltersAndNavigate(['os'], filters);

        const applyGoalFilters = (filters) =>
                applyFiltersAndNavigate(['goal'], filters);

        const applySourceFilters = (filters) =>
                applyFiltersAndNavigate(['source', 'channel', 'referrer'], filters);

        const copyScript = () => {
                const script = `<script defer data-tracking-id="${website.tracking_id}" src="${BACKEND_URL}/api/js/script.js"></script>`;
                navigator.clipboard.writeText(script);
                setCopied(true);
                toast.success('Script copied!');
                setTimeout(() => setCopied(false), 2000);
        };

        if (loading) {
                return <div className="loading">Loading...</div>;
        }

        if (!website) {
                return <div className="loading">Website not found</div>;
        }

        const formatDuration = (seconds) => {
                // Null when ?comparison=none disables prev_*. Render dash so the
                // "Previous" footer column degrades gracefully instead of "NaNm NaNs".
                if (seconds == null) return '—';
                const m = Math.floor(seconds / 60);
                const s = Math.floor(seconds % 60);
                return `${m}m ${s}s`;
        };

        const calculateChange = (current, previous) => {
                // When comparison is disabled the backend returns null for prev_*.
                // Return a safe {percent: null, positive: true} so the JSX can
                // detect "no comparison available" and render an em-dash instead
                // of a misleading "0%" or, worse, NaN.
                if (previous === null || previous === undefined) {
                        return { percent: null, positive: true };
                }
                if (previous === 0) return { percent: 0, positive: true };
                const change = ((current - previous) / previous) * 100;
                return {
                        percent: Math.abs(Math.round(change)),
                        positive: change >= 0
                };
        };

        // Safe number formatter for prev_* fields — `null`/`undefined` becomes
        // an em-dash. Used everywhere the comparison "previous value" prints
        // in a metric card so we don't crash when ?comparison=none disables prev_*.
        const formatPrev = (v, suffix = '') => (v == null ? '—' : `${v.toLocaleString()}${suffix}`);

        const progressPct = (current, previous) => {
                const c = Number(current) || 0;
                const p = Number(previous) || 0;
                const max = Math.max(c, p);
                if (max === 0) return 0;
                return Math.min(100, Math.max(0, (c / max) * 100));
        };

        const periodChipLabel = ({
                today: '24H',
                realtime: 'LIVE',
                yesterday: '1D',
                '7d': '7D',
                '30d': '30D',
                '6mo': '6M',
                '12mo': '12M',
                month: 'MTD',
                year: 'YTD',
                custom: 'CUSTOM',
        })[period] || (period ? String(period).toUpperCase() : 'PERIOD');

        const getCountryFlag = (countryCode) => {
                const countryFlags = {
                        'US': '🇺🇸',
                        'UK': '🇬🇧',
                        'CA': '🇨🇦',
                        'DE': '🇩🇪',
                        'FR': '🇫🇷',
                        'JP': '🇯🇵',
                        'AU': '🇦🇺',
                        'IN': '🇮🇳',
                        'BR': '🇧🇷',
                        'CN': '🇨🇳'
                };
                return countryFlags[countryCode] || '🌍';
        };



        const getCountryName = (countryCode) => {
                const countryNames = {
                        'US': 'United States',
                        'UK': 'United Kingdom',
                        'CA': 'Canada',
                        'DE': 'Germany',
                        'FR': 'France',
                        'JP': 'Japan',
                        'AU': 'Australia',
                        'IN': 'India',
                        'BR': 'Brazil',
                        'CN': 'China'
                };
                return countryNames[countryCode] || countryCode;
        };


        // Handle filter clicks
        const handleDeviceClick = (deviceName) => {
                // Build URL with new unified filter format
                let url = `${routeBase}?period=${period}`;
                if (startDate && endDate) {
                        url += `&start_date=${startDate}&end_date=${endDate}`;
                }
                
                // Keep existing f filters
                pageFilters.forEach(f => {
                        url += `&f=${f.condition},${f.type},${encodeURIComponent(f.value)}`;
                });
                
                // Add new device filter in unified format
                url += `&f=is,device,${encodeURIComponent(deviceName)}`;
                navigate(url);
        };

        const handleOsClick = (osName) => {
                // Build URL with new unified filter format
                let url = `${routeBase}?period=${period}`;
                if (startDate && endDate) {
                        url += `&start_date=${startDate}&end_date=${endDate}`;
                }
                
                // Keep existing f filters
                pageFilters.forEach(f => {
                        url += `&f=${f.condition},${f.type},${encodeURIComponent(f.value)}`;
                });
                
                // Add new OS filter in unified format
                url += `&f=is,os,${encodeURIComponent(osName)}`;
                navigate(url);
        };

        const handleBrowserClick = (browserName) => {
                // Build URL with new unified filter format
                let url = `${routeBase}?period=${period}`;
                if (startDate && endDate) {
                        url += `&start_date=${startDate}&end_date=${endDate}`;
                }
                
                // Keep existing f filters
                pageFilters.forEach(f => {
                        url += `&f=${f.condition},${f.type},${encodeURIComponent(f.value)}`;
                });
                
                // Add new browser filter in unified format
                url += `&f=is,browser,${encodeURIComponent(browserName)}`;
                navigate(url);
        };

        return (
                <div className="analytics-page">
                        {/* Header — public viewers get a slim chrome with logo + auth CTAs
                            instead of the in-app user dropdown. */}
                        {publicMode ? (
                                <div className="public-analytics-topbar">
                                        <div className="public-analytics-topbar__inner">
                                                <a href="/" className="public-analytics-topbar__brand">
                                                        <img src="/images/orbit-logo.svg" alt="Orbit" className="public-analytics-topbar__logo" />
                                                        <span className="public-analytics-topbar__name">Orbit Analytics</span>
                                                </a>
                                                <div className="public-analytics-topbar__cta">
                                                        <a href="/auth" className="public-analytics-topbar__btn public-analytics-topbar__btn--ghost">Login</a>
                                                        <a href="/auth?mode=signup" className="public-analytics-topbar__btn public-analytics-topbar__btn--primary">Sign Up</a>
                                                </div>
                                        </div>
                                </div>
                        ) : (
                                <HeaderBar user={user} logout={logout} />
                        )}
                        <div className="plausible-header">
                                <div className="plausible-header-content">
                                        <div className="header-top">
                                                <div className="site-selector">
                                                        <Globe className="site-icon" />
                                                        <span className="site-name" data-testid="website-domain">{website.domain}</span>
                                                        {accessRole && !accessRole.is_owner && (
                                                                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                                                                        accessRole.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                                                                        accessRole.role === 'editor' ? 'bg-blue-100 text-blue-700' :
                                                                        accessRole.role === 'billing' ? 'bg-green-100 text-green-700' :
                                                                        'bg-gray-100 text-gray-600'
                                                                }`}>
                                                                        {accessRole.role}
                                                                </span>
                                                        )}
                                                        <ChevronDown className="chevron-icon" />
                                                </div>
                                                <div className="header-actions">
                                                        <span className="current-visitors" data-testid="realtime-visitors">
                                                                <span className="visitor-dot"></span>
                                                                {realtime?.active_visitors || 0} current visitors
                                                        </span>
                                                        {!publicMode && (
                                                                <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>← Back</Button>
                                                        )}
                                                        
                                                        {/* Filter Button with Dropdown */}
                                                        <div ref={filterRef} style={{ position: 'relative' }}>
                                                                <Button 
                                                                        variant="outline" 
                                                                        size="sm"
                                                                        onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                                                                        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                                                                >
                                                                        <Search size={16} />
                                                                        Filter
                                                                </Button>
                                                                
                                                                {/* Filter Dropdown */}
                                                                {showFilterDropdown && (
                                                                        <div style={{
                                                                                position: 'absolute',
                                                                                top: '100%',
                                                                                right: 0,
                                                                                marginTop: '8px',
                                                                                backgroundColor: 'white',
                                                                                borderRadius: '8px',
                                                                                boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                                                                padding: '20px 24px',
                                                                                zIndex: 1000,
                                                                                minWidth: '320px',
                                                                                display: 'grid',
                                                                                gridTemplateColumns: '1fr 1fr',
                                                                                gap: '20px'
                                                                        }}>
                                                                                {/* URL Column */}
                                                                                <div>
                                                                                        <h4 style={{ color: '#6366f1', fontWeight: 600, fontSize: '12px', marginBottom: '12px', textTransform: 'uppercase' }}>URL</h4>
                                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                                                                <span 
                                                                        style={{ color: '#374151', fontSize: '14px', cursor: 'pointer' }} 
                                                                        className="filter-item"
                                                                        onClick={() => {
                                                                                setShowFilterDropdown(false);
                                                                                setShowPageFilterModal(true);
                                                                        }}
                                                                        data-testid="filter-page-btn"
                                                                >
                                                                        Page
                                                                </span>
                                                                                                {/* <span style={{ color: '#374151', fontSize: '14px', cursor: 'pointer' }} className="filter-item">Hostname</span> */}
                                                                                        </div>
                                                                                        
                                                                                        <h4 style={{ color: '#6366f1', fontWeight: 600, fontSize: '12px', marginTop: '20px', marginBottom: '12px', textTransform: 'uppercase' }}>Acquisition</h4>
                                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                                                                <span 
                                                                                                        style={{ color: '#374151', fontSize: '14px', cursor: 'pointer' }} 
                                                                                                        className="filter-item"
                                                                                                        onClick={() => {
                                                                                                                setShowFilterDropdown(false);
                                                                                                                setShowSourceFilterModal(true);
                                                                                                        }}
                                                                                                        data-testid="filter-source-btn"
                                                                                                >
                                                                                                        Source
                                                                                                </span>
                                                                                                <span style={{ color: '#374151', fontSize: '14px', cursor: 'pointer' }} className="filter-item">UTM tags</span>
                                                                                        </div>
                                                                                </div>
                                                                                
                                                                                {/* Device Column */}
                                                                                <div>
                                                                                        <h4 style={{ color: '#6366f1', fontWeight: 600, fontSize: '12px', marginBottom: '12px', textTransform: 'uppercase' }}>Device</h4>
                                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                                                                <span 
                                                                                                        style={{ color: '#374151', fontSize: '14px', cursor: 'pointer' }} 
                                                                                                        className="filter-item"
                                                                                                        onClick={() => {
                                                                                                                setShowFilterDropdown(false);
                                                                                                                setShowLocationFilterModal(true);
                                                                                                        }}
                                                                                                        data-testid="filter-location-btn"
                                                                                                >
                                                                                                        Location
                                                                                                </span>
                                                                                                <span style={{ color: '#374151', fontSize: '14px', cursor: 'pointer' }}
                                                                                                        className="filter-item" onClick={() => {
                                                                                                                setShowFilterDropdown(false);
                                                                                                        setShowDeviceFilterModal(true);
                                                                                                        }} >Screen size</span>
                                                                                                <span onClick={() => {
                                                                                                                setShowFilterDropdown(false);
                                                                                                                setShowBrowserFilterModal(true);
                                                                                                        }} style={{ color: '#374151', fontSize: '14px', cursor: 'pointer' }}>Browser</span>
                                                                                                <span onClick={() => {
                                                                                                                setShowFilterDropdown(false);
                                                                                                                setShowOsFilterModal(true);
                                                                                                        }} className="filter-item">Operating system</span>
                                                                                        </div>
                                                                                        
                                                                                        <h4 style={{ color: '#6366f1', fontWeight: 600, fontSize: '12px', marginTop: '20px', marginBottom: '12px', textTransform: 'uppercase' }}>Behaviour</h4>
                                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                                                                <span 
                                                                                                        style={{ color: '#374151', fontSize: '14px', cursor: 'pointer' }} 
                                                                                                        className="filter-item"
                                                                                                        onClick={() => {
                                                                                                                setShowFilterDropdown(false);
                                                                                                                setShowGoalFilterModal(true);
                                                                                                        }}
                                                                                                        data-testid="filter-goal-btn"
                                                                                                >
                                                                                                        Goal
                                                                                                </span>
                                                                                                <span style={{ color: '#374151', fontSize: '14px', cursor: 'pointer' }} className="filter-item">Property</span>
                                                                                        </div>
                                                                                </div>
                                                                        </div>
                                                                )}
                                                        </div>
                                                        
                                                        {canViewSettings && (
                                                                <Button variant="outline" size="sm" onClick={() => navigate(`/settings/${websiteId}`)}>Settings</Button>
                                                        )}
                                                        {canManageBilling && !canViewSettings && (
                                                                <Button variant="outline" size="sm" onClick={() => navigate('/settings/subscription')}>Billing</Button>
                                                        )}
                                                </div>
                                        </div>
                                </div>
                        </div>

                        <div className="plausible-content">

                                {/* Channel Filter Indicator */}
                                {channel && (
                                        <div className="channel-filter-banner">
                                                <span>Channel is: <strong>{channel}</strong></span>
                                                <button onClick={() => {
                                                        let url = `${routeBase}?period=${period}`;
                                                        if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
                                                        if (location) url += `&location=${encodeURIComponent(location)}`;
                                                        if (device) url += `&device=${encodeURIComponent(device)}`;
                                                        if (os) url += `&os=${encodeURIComponent(os)}`;
                                                        if (browser) url += `&browser=${encodeURIComponent(browser)}`;

                                                        navigate(url);
                                                }} className="clear-filter-btn">
                                                        ✕ Clear filter
                                                </button>
                                        </div>
                                )}

                                {/* Location Filter Indicator */}
                                {location && (
                                        <div className="channel-filter-banner">
                                                <span>Location is: <strong>{getCountryName(location.split(':')[1])}</strong></span>
                                                <button onClick={() => {
                                                        let url = `${routeBase}?period=${period}`;
                                                        if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
                                                        if (channel) url += `&channel=${encodeURIComponent(channel)}`;
                                                        if (device) url += `&device=${encodeURIComponent(device)}`;
                                                        if (os) url += `&os=${encodeURIComponent(os)}`;
                                                        if (browser) url += `&browser=${encodeURIComponent(browser)}`;
                                                        navigate(url);
                                                }} className="clear-filter-btn">
                                                        ✕ Clear filter
                                                </button>
                                        </div>
                                )}

                                {/* Device Filter Indicator */}
                                {device && (
                                        <div className="channel-filter-banner">
                                                <span>Device is: <strong>{device}</strong></span>
                                                <button onClick={() => {
                                                        let url = `${routeBase}?period=${period}`;
                                                        if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
                                                        if (channel) url += `&channel=${encodeURIComponent(channel)}`;
                                                        if (location) url += `&location=${encodeURIComponent(location)}`;
                                                        if (os) url += `&os=${encodeURIComponent(os)}`;
                                                        if (browser) url += `&browser=${encodeURIComponent(browser)}`;
                                                        navigate(url);
                                                }} className="clear-filter-btn">
                                                        ✕ Clear filter
                                                </button>
                                        </div>
                                )}

                                {/* OS Filter Indicator */}
                                {os && (
                                        <div className="channel-filter-banner">
                                                <span>OS is: <strong>{os}</strong></span>
                                                <button onClick={() => {
                                                        let url = `${routeBase}?period=${period}`;
                                                        if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
                                                        if (channel) url += `&channel=${encodeURIComponent(channel)}`;
                                                        if (location) url += `&location=${encodeURIComponent(location)}`;
                                                        if (device) url += `&device=${encodeURIComponent(device)}`;
                                                        if (browser) url += `&browser=${encodeURIComponent(browser)}`;
                                                        navigate(url);
                                                }} className="clear-filter-btn">
                                                        ✕ Clear filter
                                                </button>
                                        </div>
                                )}

                                {/* Browser Filter Indicator */}
                                {browser && (
                                        <div className="channel-filter-banner">
                                                <span>Browser is: <strong>{browser}</strong></span>
                                                <button onClick={() => {
                                                        let url = `${routeBase}?period=${period}`;
                                                        if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
                                                        if (channel) url += `&channel=${encodeURIComponent(channel)}`;
                                                        if (location) url += `&location=${encodeURIComponent(location)}`;
                                                        if (device) url += `&device=${encodeURIComponent(device)}`;
                                                        if (os) url += `&os=${encodeURIComponent(os)}`;
                                                        if (goal) url += `&goal=${encodeURIComponent(goal)}`;
                                                        navigate(url);
                                                }} className="clear-filter-btn">
                                                        ✕ Clear filter
                                                </button>
                                        </div>
                                )}

                                {/* Goal Filter Indicator */}
                                {goal && (
                                        <div className="channel-filter-banner">
                                                <span>Goal: <strong>{goal.split(':')[1] || goal}</strong></span>
                                                <button onClick={() => {
                                                        let url = `${routeBase}?period=${period}`;
                                                        if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
                                                        if (channel) url += `&channel=${encodeURIComponent(channel)}`;
                                                        if (location) url += `&location=${encodeURIComponent(location)}`;
                                                        if (device) url += `&device=${encodeURIComponent(device)}`;
                                                        if (os) url += `&os=${encodeURIComponent(os)}`;
                                                        if (browser) url += `&browser=${encodeURIComponent(browser)}`;
                                                        navigate(url);
                                                }} className="clear-filter-btn">
                                                        ✕ Clear filter
                                                </button>
                                        </div>
                                )}

                                {/* Page Filter Indicators - Now shows ALL unified filters */}
                                <FilterBanners filters={pageFilters} onClearFilter={clearPageFilter} />


                                {/* Metrics Cards — Option D (soft-budget) */}
                                <div className="metrics-row">
                                        {/* When goal filter is active, show conversion-focused metrics */}
                                        {activeGoalFilter ? (
                                                <>
                                                        <div
                                                                className={`metric-card ${chartMode === 'visitors' ? 'active' : ''}`}
                                                                onClick={() => handleMetricClick('visitors')}
                                                                data-testid="metric-card-unique-conversions"
                                                        >
                                                                <div className="metric-card-top">
                                                                        <div className="metric-label">Unique Conversions</div>
                                                                        <span className="metric-period-chip">{periodChipLabel}</span>
                                                                </div>
                                                                <div className="metric-value" data-testid="unique-conversions">
                                                                        {goalConversionData?.unique_visitors?.toLocaleString() || '0'}
                                                                </div>
                                                                <div className="metric-subtitle">Current Period</div>
                                                                <div className="metric-progress">
                                                                        <div className="metric-progress-bar" style={{ width: `${(goalConversionData?.unique_visitors || 0) > 0 ? 100 : 0}%` }} />
                                                                </div>
                                                        </div>

                                                        <div
                                                                className={`metric-card ${chartMode === 'visits' ? 'active' : ''}`}
                                                                onClick={() => handleMetricClick('visits')}
                                                                data-testid="metric-card-total-conversions"
                                                        >
                                                                <div className="metric-card-top">
                                                                        <div className="metric-label">Total Conversions</div>
                                                                        <span className="metric-period-chip">{periodChipLabel}</span>
                                                                </div>
                                                                <div className="metric-value">
                                                                        {goalConversionData?.total_conversions?.toLocaleString() || '0'}
                                                                </div>
                                                                <div className="metric-subtitle">Current Period</div>
                                                                <div className="metric-progress">
                                                                        <div className="metric-progress-bar" style={{ width: `${(goalConversionData?.total_conversions || 0) > 0 ? 100 : 0}%` }} />
                                                                </div>
                                                        </div>

                                                        <div
                                                                className={`metric-card ${chartMode === 'conversion_rate' ? 'active' : ''}`}
                                                                onClick={() => handleMetricClick('conversion_rate')}
                                                                data-testid="metric-card-conversion-rate"
                                                        >
                                                                <div className="metric-card-top">
                                                                        <div className="metric-label">Conversion Rate</div>
                                                                        <span className="metric-period-chip">{periodChipLabel}</span>
                                                                </div>
                                                                <div className="metric-value">
                                                                        {goalConversionData?.conversion_rate || '0'}<span style={{ fontSize: '0.875rem' }}>%</span>
                                                                </div>
                                                                <div className="metric-subtitle">Current Period</div>
                                                                <div className="metric-progress">
                                                                        <div className="metric-progress-bar" style={{ width: `${Math.min(100, Number(goalConversionData?.conversion_rate) || 0)}%` }} />
                                                                </div>
                                                        </div>
                                                </>
                                        ) : (
                                                <>
                                                        <div
                                                                className={`metric-card ${chartMode === 'visitors' ? 'active' : ''}`}
                                                                onClick={() => handleMetricClick('visitors')}
                                                                data-testid="metric-card-visitors"
                                                        >
                                                                <div className="metric-card-top">
                                                                        <div className="metric-label">Unique Visitors</div>
                                                                        <span className="metric-period-chip">{periodChipLabel}</span>
                                                                </div>
                                                                <div className="metric-value" data-testid="unique-visitors">
                                                                        {analytics.unique_visitors.toLocaleString()}
                                                                </div>
                                                                <div className="metric-subtitle">
                                                                        vs Previous Period
                                                                        <span className={`metric-change ${calculateChange(analytics.unique_visitors, analytics.prev_unique_visitors).positive ? 'positive' : 'negative'}`}>
                                                                                {calculateChange(analytics.unique_visitors, analytics.prev_unique_visitors).positive ?
                                                                                        <ArrowUpRight className="change-icon" /> : <ArrowDownRight className="change-icon" />}
                                                                                {calculateChange(analytics.unique_visitors, analytics.prev_unique_visitors).percent}%
                                                                        </span>
                                                                </div>
                                                                <div className="metric-progress">
                                                                        <div className="metric-progress-bar" style={{ width: `${progressPct(analytics.unique_visitors, analytics.prev_unique_visitors)}%` }} />
                                                                </div>
                                                                <div className="metric-footer">
                                                                        <div>
                                                                                <div className="metric-footer-label">Current</div>
                                                                                <div className="metric-footer-value">{analytics.unique_visitors.toLocaleString()}</div>
                                                                        </div>
                                                                        <div className="metric-footer-col-right">
                                                                                <div className="metric-footer-label">Previous</div>
                                                                                <div className="metric-footer-value">{formatPrev(analytics.prev_unique_visitors)}</div>
                                                                        </div>
                                                                </div>
                                                        </div>

                                                        <div
                                                                className={`metric-card ${chartMode === 'visits' ? 'active' : ''}`}
                                                                onClick={() => handleMetricClick('visits')}
                                                                data-testid="metric-card-visits"
                                                        >
                                                                <div className="metric-card-top">
                                                                        <div className="metric-label">Total Visits</div>
                                                                        <span className="metric-period-chip">{periodChipLabel}</span>
                                                                </div>
                                                                <div className="metric-value">{analytics.total_visits.toLocaleString()}</div>
                                                                <div className="metric-subtitle">
                                                                        vs Previous Period
                                                                        <span className={`metric-change ${calculateChange(analytics.total_visits, analytics.prev_total_visits).positive ? 'positive' : 'negative'}`}>
                                                                                {calculateChange(analytics.total_visits, analytics.prev_total_visits).positive ?
                                                                                        <ArrowUpRight className="change-icon" /> : <ArrowDownRight className="change-icon" />}
                                                                                {calculateChange(analytics.total_visits, analytics.prev_total_visits).percent}%
                                                                        </span>
                                                                </div>
                                                                <div className="metric-progress">
                                                                        <div className="metric-progress-bar" style={{ width: `${progressPct(analytics.total_visits, analytics.prev_total_visits)}%` }} />
                                                                </div>
                                                                <div className="metric-footer">
                                                                        <div>
                                                                                <div className="metric-footer-label">Current</div>
                                                                                <div className="metric-footer-value">{analytics.total_visits.toLocaleString()}</div>
                                                                        </div>
                                                                        <div className="metric-footer-col-right">
                                                                                <div className="metric-footer-label">Previous</div>
                                                                                <div className="metric-footer-value">{formatPrev(analytics.prev_total_visits)}</div>
                                                                        </div>
                                                                </div>
                                                        </div>

                                                        <div
                                                                className={`metric-card ${chartMode === 'pageviews' ? 'active' : ''}`}
                                                                onClick={() => handleMetricClick('pageviews')}
                                                                data-testid="metric-card-pageviews"
                                                        >
                                                                <div className="metric-card-top">
                                                                        <div className="metric-label">Total Pageviews</div>
                                                                        <span className="metric-period-chip">{periodChipLabel}</span>
                                                                </div>
                                                                <div className="metric-value" data-testid="total-pageviews">{analytics.total_pageviews.toLocaleString()}</div>
                                                                <div className="metric-subtitle">
                                                                        vs Previous Period
                                                                        <span className={`metric-change ${calculateChange(analytics.total_pageviews, analytics.prev_total_pageviews).positive ? 'positive' : 'negative'}`}>
                                                                                {calculateChange(analytics.total_pageviews, analytics.prev_total_pageviews).positive ?
                                                                                        <ArrowUpRight className="change-icon" /> : <ArrowDownRight className="change-icon" />}
                                                                                {calculateChange(analytics.total_pageviews, analytics.prev_total_pageviews).percent}%
                                                                        </span>
                                                                </div>
                                                                <div className="metric-progress">
                                                                        <div className="metric-progress-bar" style={{ width: `${progressPct(analytics.total_pageviews, analytics.prev_total_pageviews)}%` }} />
                                                                </div>
                                                                <div className="metric-footer">
                                                                        <div>
                                                                                <div className="metric-footer-label">Current</div>
                                                                                <div className="metric-footer-value">{analytics.total_pageviews.toLocaleString()}</div>
                                                                        </div>
                                                                        <div className="metric-footer-col-right">
                                                                                <div className="metric-footer-label">Previous</div>
                                                                                <div className="metric-footer-value">{formatPrev(analytics.prev_total_pageviews)}</div>
                                                                        </div>
                                                                </div>
                                                        </div>

                                                        <div
                                                                className={`metric-card ${chartMode === 'views_per_visit' ? 'active' : ''}`}
                                                                onClick={() => handleMetricClick('views_per_visit')}
                                                                data-testid="metric-card-views-per-visit"
                                                        >
                                                                <div className="metric-card-top">
                                                                        <div className="metric-label">Views / Visit</div>
                                                                        <span className="metric-period-chip">{periodChipLabel}</span>
                                                                </div>
                                                                <div className="metric-value">{analytics.views_per_visit}</div>
                                                                <div className="metric-subtitle">
                                                                        vs Previous Period
                                                                        <span className={`metric-change ${calculateChange(analytics.views_per_visit, analytics.prev_views_per_visit).positive ? 'positive' : 'negative'}`}>
                                                                                {calculateChange(analytics.views_per_visit, analytics.prev_views_per_visit).positive ?
                                                                                        <ArrowUpRight className="change-icon" /> : <ArrowDownRight className="change-icon" />}
                                                                                {calculateChange(analytics.views_per_visit, analytics.prev_views_per_visit).percent}%
                                                                        </span>
                                                                </div>
                                                                <div className="metric-progress">
                                                                        <div className="metric-progress-bar" style={{ width: `${progressPct(analytics.views_per_visit, analytics.prev_views_per_visit)}%` }} />
                                                                </div>
                                                                <div className="metric-footer">
                                                                        <div>
                                                                                <div className="metric-footer-label">Current</div>
                                                                                <div className="metric-footer-value">{analytics.views_per_visit}</div>
                                                                        </div>
                                                                        <div className="metric-footer-col-right">
                                                                                <div className="metric-footer-label">Previous</div>
                                                                                <div className="metric-footer-value">{analytics.prev_views_per_visit ?? '—'}</div>
                                                                        </div>
                                                                </div>
                                                        </div>

                                                        <div
                                                                className={`metric-card ${chartMode === 'bounce_rate' ? 'active' : ''}`}
                                                                onClick={() => handleMetricClick('bounce_rate')}
                                                                data-testid="metric-card-bounce-rate"
                                                        >
                                                                <div className="metric-card-top">
                                                                        <div className="metric-label">Bounce Rate</div>
                                                                        <span className="metric-period-chip">{periodChipLabel}</span>
                                                                </div>
                                                                <div className="metric-value">{analytics.bounce_rate}<span style={{ fontSize: '0.875rem' }}>%</span></div>
                                                                <div className="metric-subtitle">
                                                                        vs Previous Period
                                                                        <span className={`metric-change ${calculateChange(analytics.prev_bounce_rate, analytics.bounce_rate).positive ? 'positive' : 'negative'}`}>
                                                                                {calculateChange(analytics.prev_bounce_rate, analytics.bounce_rate).positive ?
                                                                                        <ArrowUpRight className="change-icon" /> : <ArrowDownRight className="change-icon" />}
                                                                                {calculateChange(analytics.prev_bounce_rate, analytics.bounce_rate).percent}%
                                                                        </span>
                                                                </div>
                                                                <div className="metric-progress">
                                                                        <div className="metric-progress-bar" style={{ width: `${progressPct(analytics.bounce_rate, analytics.prev_bounce_rate)}%` }} />
                                                                </div>
                                                                <div className="metric-footer">
                                                                        <div>
                                                                                <div className="metric-footer-label">Current</div>
                                                                                <div className="metric-footer-value">{analytics.bounce_rate}%</div>
                                                                        </div>
                                                                        <div className="metric-footer-col-right">
                                                                                <div className="metric-footer-label">Previous</div>
                                                                                <div className="metric-footer-value">{formatPrev(analytics.prev_bounce_rate, '%')}</div>
                                                                        </div>
                                                                </div>
                                                        </div>

                                                        <div
                                                                className={`metric-card ${chartMode === 'duration' ? 'active' : ''}`}
                                                                onClick={() => handleMetricClick('duration')}
                                                                data-testid="metric-card-duration"
                                                        >
                                                                <div className="metric-card-top">
                                                                        <div className="metric-label">Visit Duration</div>
                                                                        <span className="metric-period-chip">{periodChipLabel}</span>
                                                                </div>
                                                                <div className="metric-value">{formatDuration(analytics.avg_duration)}</div>
                                                                <div className="metric-subtitle">
                                                                        vs Previous Period
                                                                        <span className={`metric-change ${calculateChange(analytics.avg_duration, analytics.prev_avg_duration).positive ? 'positive' : 'negative'}`}>
                                                                                {calculateChange(analytics.avg_duration, analytics.prev_avg_duration).positive ?
                                                                                        <ArrowUpRight className="change-icon" /> : <ArrowDownRight className="change-icon" />}
                                                                                {calculateChange(analytics.avg_duration, analytics.prev_avg_duration).percent}%
                                                                        </span>
                                                                </div>
                                                                <div className="metric-progress">
                                                                        <div className="metric-progress-bar" style={{ width: `${progressPct(analytics.avg_duration, analytics.prev_avg_duration)}%` }} />
                                                                </div>
                                                                <div className="metric-footer">
                                                                        <div>
                                                                                <div className="metric-footer-label">Current</div>
                                                                                <div className="metric-footer-value">{formatDuration(analytics.avg_duration)}</div>
                                                                        </div>
                                                                        <div className="metric-footer-col-right">
                                                                                <div className="metric-footer-label">Previous</div>
                                                                                <div className="metric-footer-value">{formatDuration(analytics.prev_avg_duration)}</div>
                                                                        </div>
                                                                </div>
                                                        </div>
                                                </>
                                        )}
                                </div>

                                {/* Chart */}
                                <div className="chart-section">
                                        <div className="chart-controls">
                                                <div className="chart-tabs">
                                                        <button className="chart-tab active">
                                                                {activeGoalFilter ? (
                                                                        <>
                                                                                {chartMode === 'visitors' && 'Unique Conversions'}
                                                                                {chartMode === 'visits' && 'Total Conversions'}
                                                                                {chartMode === 'conversion_rate' && 'Conversion Rate'}
                                                                        </>
                                                                ) : (
                                                                        <>
                                                                                {chartMode === 'visitors' && 'Unique Visitors'}
                                                                                {chartMode === 'visits' && 'Total Visits'}
                                                                                {chartMode === 'pageviews' && 'Pageviews'}
                                                                                {chartMode === 'views_per_visit' && 'Views per Visit'}
                                                                                {chartMode === 'bounce_rate' && 'Bounce Rate'}
                                                                                {chartMode === 'duration' && 'Visit Duration'}
                                                                        </>
                                                                )}
                                                        </button>
                                                </div>
                                                {/* When comparison is on, the single PeriodSelector becomes
                                                    a [Period ▾] vs [Comparison ▾] dual-pill (image 2). */}
                                                <div className="period-row">
                                                        <PeriodSelector />
                                                        {comparisonActive && (
                                                                <>
                                                                        <span className="period-row__vs">vs</span>
                                                                        <ComparisonPicker />
                                                                </>
                                                        )}
                                                </div>
                                        </div>
                                        <div className="chart-wrapper" style={{ position: 'relative', minHeight: '300px' }}>
                                                {chartLoading && (
                                                        <div data-testid="chart-loader" style={{
                                                                position: 'absolute',
                                                                inset: 0,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                background: 'rgba(255,255,255,0.7)',
                                                                zIndex: 10,
                                                                borderRadius: '8px',
                                                                backdropFilter: 'blur(2px)'
                                                        }}>
                                                                <div style={{
                                                                        width: '36px',
                                                                        height: '36px',
                                                                        border: '3px solid #e5e7eb',
                                                                        borderTop: '3px solid #6366f1',
                                                                        borderRadius: '50%',
                                                                        animation: 'spin 0.7s linear infinite'
                                                                }} />
                                                        </div>
                                                )}
                                                {analytics.chart_data && analytics.chart_data.length > 0 && (
                                                        <AreaChart
                                                                labels={analytics.chart_data.map(d => d[0])}
                                                                values={analytics.chart_data.map(d => d[1])}
                                                                /* Comparison series — present only when ?comparison= is set
                                                                   and the backend computed prev_chart_data. Pass both the
                                                                   prev VALUES (for the second line) and the prev LABELS so
                                                                   the tooltip can show "Thu, 14 May" beside "Thu, 21 May" —
                                                                   matching Plausible's day-aligned comparison tooltip. */
                                                                prevValues={comparisonActive && analytics.prev_chart_data ? analytics.prev_chart_data.map(d => d[1]) : null}
                                                                prevLabels={comparisonActive && analytics.prev_chart_data ? analytics.prev_chart_data.map(d => d[0]) : null}
                                                                label={
                                                                        activeGoalFilter ? (
                                                                                chartMode === 'visitors' ? 'Conversions' :
                                                                                chartMode === 'visits' ? 'Conversions' :
                                                                                chartMode === 'conversion_rate' ? 'Rate (%)' : 'Conversions'
                                                                        ) : (
                                                                                chartMode === 'visitors' ? 'Visitors' :
                                                                                chartMode === 'visits' ? 'Visits' :
                                                                                chartMode === 'pageviews' ? 'Pageviews' :
                                                                                chartMode === 'views_per_visit' ? 'Views per Visit' :
                                                                                chartMode === 'bounce_rate' ? 'Bounce Rate' :
                                                                                chartMode === 'duration' ? 'Visit Duration' : 'Visitors'
                                                                        )
                                                                }
                                                                period={period}
                                                        />
                                                )}
                                        </div>
                                </div>

                                {/* Tables Section */}
                                <div className="data-tables">
                                        {/* Traffic Insights - Dynamic Channels/Sources/Campaigns */}

                                        <TrafficInsights
                                                websiteId={websiteId}
                                                period={period}
                                                startDate={startDate}
                                                endDate={endDate}
                                                channel={channel}
                                                location={location}
                                                device={device}
                                                os={os}
                                                goal={goal}
                                                browser={browser}
                                                token={token}
                                                existingFilters={searchParams.getAll('f')}
                                        />

                                        {/* Pages Insights - Top/Entry/Exit Pages */}
                                        <PagesInsights
                                                websiteId={websiteId}
                                                websiteDomain={website?.domain}
                                                period={period}
                                                startDate={startDate}
                                                endDate={endDate}
                                                channel={channel}
                                                location={location}
                                                device={device}
                                                os={os}
                                                goal={goal}
                                                activeGoalFilter={activeGoalFilter}
                                                browser={browser}
                                                token={token}
                                                analytics={analytics}
                                                existingFilters={searchParams.getAll('f')}
                                        />
                                </div>

                                {/* Locations and Devices Section */}
                                <div className="data-tables">
                                        {/* Locations Insights - Map/Countries/Regions/Cities */}
                                        <LocationsInsights
                                                websiteId={websiteId}
                                                period={period}
                                                startDate={startDate}
                                                endDate={endDate}
                                                channel={channel}
                                                device={device}
                                                os={os}
                                                goal={goal}
                                                browser={browser}
                                                token={token}
                                                analytics={analytics}
                                                existingFilters={searchParams.getAll('f')}
                                        />

                                        {/* Devices - Extracted to component */}
                                        <DevicesInsights
                                                analytics={analytics}
                                                onDeviceClick={handleDeviceClick}
                                                onBrowserClick={handleBrowserClick}
                                                onOsClick={handleOsClick}
                                                websiteId={websiteId}
                                                period={period}
                                                startDate={startDate}
                                                endDate={endDate}
                                                channel={channel}
                                                location={location}
                                                device={device}
                                                os={os}
                                                browser={browser}
                                                token={token}
                                                existingFilters={searchParams.getAll('f')}
                                        />
                                </div>

                                {/* Goal Conversions Section */}
                                <div className="chart-section padd-0">
                                        <GoalConversions
                                                websiteId={websiteId}
                                                period={period}
                                                startDate={startDate}
                                                endDate={endDate}
                                                channel={channel}
                                                location={location}
                                                device={device}
                                                os={os}
                                                browser={browser}
                                                goal={goal}
                                                activeGoalFilter={activeGoalFilter}
                                                token={token}
                                                pageFilters={pageFilters}
                                                existingFilters={searchParams.getAll('f')}
                                        />
                                </div>

                                {/* Search Queries Section - Google Search Console.
                                    Hidden in publicMode: GSC integration is owner-only
                                    (uses the owner's OAuth token) and has no public viewer story. */}
                                {!publicMode && (
                                        <div className="chart-section padd-0">
                                                <SearchQueriesInsights
                                                        websiteId={websiteId}
                                                        token={token}
                                                        period={period}
                                                        startDate={startDate}
                                                        endDate={endDate}
                                                />
                                        </div>
                                )}

                                {/* Installation Card.
                                    Hidden in publicMode: the tracking-id snippet is for the
                                    site owner to install on their site, not for visitors. */}
                                {!publicMode && (
                                        <Card className="installation-card">
                                                <CardHeader>
                                                        <CardTitle>Installation Script</CardTitle>
                                                        <CardDescription>Add this tracking script to your website&apos;s &lt;head&gt; section</CardDescription>
                                                </CardHeader>
                                                <CardContent>
                                                        <div className="script-container">
                                                                <code className="tracking-script" data-testid="tracking-script">
                                                                        {`<script defer data-tracking-id="${website.tracking_id}" src="${BACKEND_URL}/api/js/script.js"></script>`}
                                                                </code>
                                                                <Button data-testid="copy-script-btn" size="sm" onClick={copyScript}>
                                                                        {copied ? <Check className="icon-sm" /> : <Copy className="icon-sm" />}
                                                                </Button>
                                                        </div>
                                                </CardContent>
                                        </Card>
                                )}
                        </div>
                        
                        {/* Page Filter Modal */}
                        <PageFilterModal
                                isOpen={showPageFilterModal}
                                onClose={() => setShowPageFilterModal(false)}
                                onApply={applyPageFilter}
                                websiteId={websiteId}
                                token={token}
                                analytics={analytics}
                                period={period}
                                startDate={startDate}
                                endDate={endDate}
                        />


                        {/* Location Filter Modal */}
                        <DeviceFilterModal
                                isOpen={showDeviceFilterModal}
                                onClose={() => setShowDeviceFilterModal(false)}
                                onApply={applyDeviceFilters}
                                websiteId={websiteId}
                                token={token}
                                period={period}
                                startDate={startDate}
                                endDate={endDate}
                                analytics={analytics}
                        />

                        
                        {/* Location Filter Modal */}
                        <LocationFilterModal
                                isOpen={showLocationFilterModal}
                                onClose={() => setShowLocationFilterModal(false)}
                                onApply={applyLocationFilters}
                                websiteId={websiteId}
                                token={token}
                                period={period}
                                startDate={startDate}
                                endDate={endDate}
                                analytics={analytics}
                        />
                        {/* Browser Filter Modal */}
                        <BrowserFilterModal
                                isOpen={showBrowserFilterModal}
                                onClose={() => setShowBrowserFilterModal(false)}
                                onApply={applyBrowserFilters}
                                websiteId={websiteId}
                                token={token}
                                period={period}
                                startDate={startDate}
                                endDate={endDate}
                                analytics={analytics}
                        />

                        {/* OS Filter Modal */}
                        <OSFilterModal
                                isOpen={showOsFilterModal}
                                onClose={() => setShowOsFilterModal(false)}
                                onApply={applyOsFilters}
                                websiteId={websiteId}
                                token={token}
                                period={period}
                                startDate={startDate}
                                endDate={endDate}
                                analytics={analytics}
                        />

                        {/* Goal Filter Modal */}
                        <GoalFilterModal
                                isOpen={showGoalFilterModal}
                                onClose={() => setShowGoalFilterModal(false)}
                                onApply={applyGoalFilters}
                                websiteId={websiteId}
                                token={token}
                        />

                        {/* Source Filter Modal */}
                        <SourceFilterModal
                                isOpen={showSourceFilterModal}
                                onClose={() => setShowSourceFilterModal(false)}
                                onApply={applySourceFilters}
                                analytics={analytics}
                        />
                </div>
        );
};

// ============== MAIN APP ==============

export default AnalyticsPage;
